/**
 * WizardSlidePanel — multi-step form wizard inside a SlidePanel.
 *
 * Public API only — see WizardInner for rendering logic, WizardStepNav for
 * sidebar navigation, WizardProgressDots for the step progress indicator.
 *
 * Note: <FormElement> is intentionally absent. The wizard submits programmatically
 * via formApi.onSubmit(e) — no native form submit is needed. Including
 * <FormElement> would create a <form> DOM node that is disconnected from the
 * portal-rendered inputs (ReactPortal moves them to a separate DOM subtree).
 */
import { FormProvider, useForm } from "react-hook-form";
import { WizardProvider } from "./context/WizardContext";
import { type WizardSlidePanelProps } from "./types";
import { useResetOnOpen } from "./useResetOnOpen";
import { WizardInner } from "./WizardInner";

function WizardSlidePanel<T = unknown>(props: WizardSlidePanelProps<T>) {
  const { initialStep = 0 } = props;

  // Validate and clamp initialStep to valid range (0..last step)
  const validInitialStep = Math.max(
    0,
    Math.min(initialStep, props.items.length - 1),
  );

  const methods = useForm<Record<string, unknown>>({
    defaultValues: (props.initialValues as Record<string, unknown>) ?? {},
    // No resolver here — field-level rules.validate (from FormInput validator=,
    // FormDropDownList validator=, HiddenValidatedField, etc.) are ignored when
    // a resolver is present. Form-level validate prop is evaluated directly in
    // WizardInner's Next/Submit handlers and combined with RHF trigger().
    // mode: "onTouched" → fields first validate on blur; once touched (including
    // programmatically by validateCurrentStep after a failed Next click), they
    // re-validate on every change. This clears errors as the user corrects them
    // without requiring handleSubmit/isSubmitted (which trigger() never sets).
    // reValidateMode: "onChange" is retained for the final step's handleSubmit path.
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  useResetOnOpen(
    methods,
    props.open,
    (props.initialValues as Record<string, unknown>) ?? {},
  );

  return (
    <WizardProvider initialStep={validInitialStep}>
      <FormProvider {...methods}>
        <WizardInner<T> {...props} />
      </FormProvider>
    </WizardProvider>
  );
}

export default WizardSlidePanel;
